import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const CLOSE_DURATION_MS = 380;

const MENU_LINKS = [
  { label: 'Store', path: '/store' },
  { label: 'About Us', path: '/about' },
  { label: 'Album', path: '/photo-booth' },
  { label: 'Blog', path: '/blogs' },
];

export default function MenuSidebar() {
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);
  const closeTimerRef = useRef(null);
  const lastNavigateAtRef = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const finishClose = (afterClose) => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setMounted(false);
      setClosing(false);
      if (typeof afterClose === 'function') afterClose();
    }, CLOSE_DURATION_MS);
  };

  const close = (afterClose) => {
    if (!mounted) {
      if (typeof afterClose === 'function') afterClose();
      return;
    }

    setClosing(true);
    setIsOpen(false);

    // Release scroll lock immediately so page remains interactive while close animation runs.
    document.documentElement.style.removeProperty('overflow');
    document.body.style.removeProperty('overflow');

    // Notify listeners (banner/nav) that menu is no longer open.
    window.dispatchEvent(
      new CustomEvent('menu:toggle', { detail: { open: false, source: 'menu-sidebar' } })
    );

    finishClose(afterClose);
  };

  const closeAndNavigate = (path) => {
    // Navigate immediately for snappy UX; let close animation finish in parallel.
    navigate(path);
    close();
  };

  const activateLink = (path) => {
    const now = Date.now();
    if (now - lastNavigateAtRef.current < 250) return;
    lastNavigateAtRef.current = now;
    closeAndNavigate(path);
  };

  useEffect(() => {
    const onToggle = (e) => {
      if (e?.detail?.source === 'menu-sidebar') return;

      const open = e?.detail?.open ?? !isOpen;
      if (open) {
        clearCloseTimer();
        setMounted(true);
        setClosing(false);
        setIsOpen(true);
      } else {
        close();
      }
    };

    const onAnySidebarOpen = (e) => {
      const open = e?.detail?.open ?? true;
      // If any other overlay opens, close menu first to avoid overlap.
      if (open && isOpen) {
        close();
      }
    };

    window.addEventListener('menu:toggle', onToggle);
    window.addEventListener('wishlist:toggle', onAnySidebarOpen);
    window.addEventListener('search:toggle', onAnySidebarOpen);
    window.addEventListener('cart:toggle', onAnySidebarOpen);
    window.addEventListener('signup:toggle', onAnySidebarOpen);

    return () => {
      clearCloseTimer();
      window.removeEventListener('menu:toggle', onToggle);
      window.removeEventListener('wishlist:toggle', onAnySidebarOpen);
      window.removeEventListener('search:toggle', onAnySidebarOpen);
      window.removeEventListener('cart:toggle', onAnySidebarOpen);
      window.removeEventListener('signup:toggle', onAnySidebarOpen);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mounted]);

  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.setProperty('overflow', 'hidden', 'important');
      document.body.style.setProperty('overflow', 'hidden', 'important');
    } else {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    }

    return () => {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    };
  }, [isOpen]);

  if (!mounted) return null;

  return (
    <div className={`fixed inset-0 z-[11001] ${isOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}>
      <div
        className={`absolute inset-0 bg-[#efefef] menu-sheet ${closing ? 'menu-sheet-close' : 'menu-sheet-open'}`}
      >
        <div className="h-full flex flex-col px-2.5 pt-2.5 pb-3">
          <div className="flex items-center justify-start">
            <button
              type="button"
              onClick={() => close()}
              className="menu-close-trigger text-[16px] leading-none font-bdogrotesk text-black/85"
            >
              Close
            </button>
          </div>

          <div className="flex-1 overflow-y-auto pt-2.5">
            <nav className="flex flex-col leading-[1.02]">
              {MENU_LINKS.map((link, index) => (
                <button
                  type="button"
                  key={link.label}
                  onPointerUp={() => activateLink(link.path)}
                  onClick={() => activateLink(link.path)}
                  className="menu-item-reveal w-full py-1 sm:py-1.5 text-left text-[72px] sm:text-[84px] tracking-[-0.045em] font-bdogrotesk font-medium text-black touch-manipulation select-none"
                  style={{ '--menu-index': index }}
                >
                  <span className={`menu-item-label ${location.pathname === link.path ? 'underline underline-offset-8 decoration-4' : ''}`}>{link.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="menu-footer-reveal pt-3.5 pb-0.5 space-y-1.5">
            <p className="text-base font-medium leading-[1.1] tracking-[-0.02em] text-black/85 font-bdogrotesk max-w-[300px]">
              We make everyday clothes for people who like dressing up everyday.
            </p>
            <div className="text-[17px] text-black/85 font-bdogrotesk">© {new Date().getFullYear()} NUFAB</div>
          </div>
        </div>
      </div>

    </div>
  );
}
